/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { useAINudges } from '@atlassian/ai-smart-button-api';
import { useConversationActionsStoreActions } from '@atlassian/rovo-conversation-actions/conversation-actions-store';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';
import { isChatAction } from '@atlassian/rovo-conversation-actions/utils';

import { useAISmartButtonContext } from '../../../../common/context';
import { useNudgeSuppression } from '../../../../common/hooks/use-nudge-suppression';
import { isBrowser } from '../../../../common/utils/is-browser';
import { isNudgeRecommendationSurface } from '../../../../common/utils/is-nudge-recommendation-surface';
import { CONVO_STARTER_ID } from '../../../../constants';
import { useFetchNudgeRecommendations } from '../../../../hooks/useFetchNudgeRecommendations';
import { findActiveNudge } from '../../../../utils/findActiveNudge';
import { useNudgeSessionState } from './use-nudge-session-state';

const messages = defineMessages({
	convoStarterSubheader: {
		id: 'ai-smart-button.convo-starter.nudge.subheader',
		defaultMessage: 'Open Rovo chat with this page-specific starter',
		description: 'Subheader text shown on hover for conversation starter nudges',
	},
});

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudge = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudge,
		}),
	),
);

type AIProactiveNudgeProps = {
	onActiveNudgeChange?: (hasActiveNudge: boolean) => void;
};

/**
 * Resolves which nudge — if any — should be shown on the Rovo button.
 *
 * Resolution order (recommendation surfaces):
 *   1. Wait for BE recommendations to load.
 *   2. BE-driven nudges (e.g. Convo Starters) win when available and eligible.
 *   3. Fall back to FE self-eligible nudges (e.g. Daily Brief) when any of the following hold:
 *        - BE returned an error
 *        - BE returned no recommendations
 *        - all BE recommendations were filtered out by dismissed/accepted/suppression
 *        - no BE recommendation matched a registered FE placeholder
 *
 * Non-recommendation surfaces (search, Jira, live doc edit) skip the BE call
 * entirely and show the first non-suppressed registered nudge.
 *
 * The resolved nudge stays active for the page load and is cleared on navigation
 * or when the active nudge is dismissed, accepted, or suppressed.
 */
export const AIProactiveNudge = ({
	onActiveNudgeChange,
}: AIProactiveNudgeProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { product, pageMode, isLiveViewMode } = useAISmartButtonContext();
	const isLiveDoc = pageMode === 'live';
	const [triggerId] = useState(() => crypto.randomUUID());

	const selectedNudges = useAINudges();
	const shouldTrigger = Boolean(selectedNudges?.length);
	const pageUrl = isBrowser() ? `${window.location.origin}${window.location.pathname}` : '';
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		shouldTrigger,
		pageUrl,
		product,
		isLiveDoc,
		isLiveViewMode,
	);
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();
	const { isSuppressed, addSuppressedNudge, addOtsSuppressedNudge } = useNudgeSuppression();
	const { setConvoStarterActionPrompt } = useConversationActionsStoreActions();

	// Hold the active nudge for the page load. Resets on navigation.
	const [activeNudge, setActiveNudge] = useState<ConversationAction | null>(null);
	const [isExitingForReplacement, setIsExitingForReplacement] = useState(false);
	const [replacementNudgeSequence, setReplacementNudgeSequence] = useState(0);
	// Keep transition refs outside the effect dependencies so clearing the active nudge
	// does not immediately re-pin the next resolved nudge on the experiment-off path.
	const activeNudgeForPageRef = useRef<ConversationAction | null>(null);
	const pendingReplacementNudgeRef = useRef<ConversationAction | null>(null);
	const prevPageUrlRef = useRef<string>(pageUrl);

	const isRecommendationSurface = useMemo(
		() => isNudgeRecommendationSurface({ product, pageUrl, isLiveDoc, isLiveViewMode }),
		[product, pageUrl, isLiveDoc, isLiveViewMode],
	);

	// FE-driven nudges the user has not already dismissed, accepted, or had suppressed for this session.
	const availableNudges = useMemo(
		() =>
			selectedNudges.filter(
				(nudge) =>
					!dismissedNudges.includes(nudge.id) &&
					!acceptedNudges.includes(nudge.id) &&
					!isSuppressed(nudge.id, nudge.nudgeContext?.suppressionKey),
			),
		[selectedNudges, dismissedNudges, acceptedNudges, isSuppressed],
	);

	// Fallback FE nudge (self-eligible) used when the BE produces no winner.
	const feFallback = useMemo(
		() => availableNudges.find((nudge) => nudge.nudgeContext?.selfEligible) ?? null,
		[availableNudges],
	);

	const resolvedNudge: ConversationAction | null = useMemo(() => {
		// Non-recommendation surfaces (search, Jira, live doc edit) skip the BE call and show the
		// first available registered nudge.
		if (!isRecommendationSurface) {
			return availableNudges[0] ?? null;
		}

		// Wait for BE recommendations before resolving so BE-driven nudges can win when eligible.
		if (loading || !selectedNudges?.length) {
			return null;
		}

		// On BE error or empty recommendations, fall back to FE self-eligible.
		if (error || !recommendations?.length) {
			return feFallback;
		}

		// TODO: dismiss/accept nudge by its nudgeContext.id so we only need to check recommendations
		const availableRecommendations = recommendations.filter((recommendation) => {
			const id = recommendation.feature_details.feature_id;
			const suppressionKey = recommendation.context?.context_id ?? undefined;
			return (
				!dismissedNudges.includes(id) &&
				!acceptedNudges.includes(id) &&
				!isSuppressed(id, suppressionKey)
			);
		});

		// If every BE recommendation was filtered out, fall back to FE self-eligible.
		if (!availableRecommendations.length) {
			return feFallback;
		}

		// If no BE recommendation matched a registered FE placeholder, fall back to FE self-eligible.
		const nudge = findActiveNudge(availableRecommendations, availableNudges) ?? null;
		if (!nudge) {
			return feFallback;
		}

		if (nudge.id === CONVO_STARTER_ID) {
			return {
				...nudge,
				secondaryTitle: formatMessage(messages.convoStarterSubheader),
			};
		}
		return nudge;
	}, [
		acceptedNudges,
		availableNudges,
		dismissedNudges,
		error,
		feFallback,
		formatMessage,
		isRecommendationSurface,
		isSuppressed,
		loading,
		recommendations,
		selectedNudges,
	]);

	const shouldWaitForRecommendations = loading && isRecommendationSurface;
	const hasActiveNudge = activeNudge !== null;
	const isAgentMentionsExperimentEnabled = expVal(
		'platform_editor_agent_mentions',
		'isEnabled',
		false,
	);
	const shouldReplaceActiveNudgeAfterExit =
		isAgentMentionsExperimentEnabled &&
		Boolean(activeNudge && resolvedNudge && resolvedNudge !== activeNudge);

	const setActiveNudgeForPage = React.useCallback((nextNudge: ConversationAction | null) => {
		activeNudgeForPageRef.current = nextNudge;
		setActiveNudge(nextNudge);
	}, []);

	const handleReplacementExitComplete = React.useCallback(() => {
		const nextNudge = pendingReplacementNudgeRef.current;
		pendingReplacementNudgeRef.current = null;
		setIsExitingForReplacement(false);
		setActiveNudgeForPage(nextNudge);
		if (nextNudge) {
			setReplacementNudgeSequence((current) => current + 1);
		}
	}, [setActiveNudgeForPage]);

	// Set the active nudge once BE finishes loading (or immediately on non-recommendation surfaces).
	// Reset on page navigation. Clear when the active nudge is dismissed/accepted/suppressed.
	useEffect(() => {
		let activeNudgeForPage = activeNudgeForPageRef.current;

		// Reset on navigation — fall through so the new page's nudge can be pinned in this same effect run.
		if (prevPageUrlRef.current !== pageUrl) {
			prevPageUrlRef.current = pageUrl;
			pendingReplacementNudgeRef.current = null;
			setIsExitingForReplacement(false);
			setActiveNudgeForPage(null);
			activeNudgeForPage = null;
		}
		// On non-recommendation surfaces, pin immediately (no BE call to wait for).
		// On recommendation surfaces, wait for BE to finish loading.
		if (shouldWaitForRecommendations) {
			return;
		}

		if (!isAgentMentionsExperimentEnabled) {
			setActiveNudge((prev) => {
				if (prev === null) {
					return resolvedNudge;
				}
				if (resolvedNudge === null || resolvedNudge.id !== prev.id) {
					return null;
				}
				return prev;
			});
			return;
		}

		if (activeNudgeForPage === null) {
			setActiveNudgeForPage(resolvedNudge);
			return;
		}

		if (resolvedNudge === null) {
			pendingReplacementNudgeRef.current = null;
			setIsExitingForReplacement(false);
			setActiveNudgeForPage(null);
			return;
		}

		// Intentionally compare object identity rather than id. Agent mention nudges can
		// reuse the same action id while representing a new mention instance/context,
		// so a new object should be allowed to exit and refire.
		if (resolvedNudge === activeNudgeForPage && !isExitingForReplacement) {
			return;
		}

		if (shouldReplaceActiveNudgeAfterExit) {
			pendingReplacementNudgeRef.current = resolvedNudge;
			setIsExitingForReplacement(true);
			return;
		}

		if (resolvedNudge.id !== activeNudgeForPage.id) {
			// Active nudge was dismissed/accepted/suppressed (anti-spam: don't replace mid-page).
			setActiveNudgeForPage(null);
		}
	}, [
		pageUrl,
		shouldWaitForRecommendations,
		resolvedNudge,
		isExitingForReplacement,
		isAgentMentionsExperimentEnabled,
		shouldReplaceActiveNudgeAfterExit,
		setActiveNudgeForPage,
	]);

	// Notify parent about active nudge state
	useEffect(() => {
		onActiveNudgeChange?.(Boolean(activeNudge));
	}, [activeNudge, onActiveNudgeChange]);

	// Sync the active nudge convo starter prompt into the shared store so the actions panel
	// can find the matching starter and pin it at position 0.
	useEffect(() => {
		if (
			activeNudge?.id === CONVO_STARTER_ID &&
			isChatAction(activeNudge) &&
			fg('aix_convo_starter_nudge_enabled')
		) {
			setConvoStarterActionPrompt(activeNudge.staticPrompt);
		} else {
			setConvoStarterActionPrompt(undefined);
		}
		return () => {
			setConvoStarterActionPrompt(undefined);
		};
	}, [activeNudge, setConvoStarterActionPrompt]);

	// Early exit if no nudge to show
	if (!hasActiveNudge) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudge
				key={`${activeNudge.id}:${replacementNudgeSequence}`}
				activeNudge={activeNudge}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
				addSuppressedNudge={addSuppressedNudge}
				addOtsSuppressedNudge={addOtsSuppressedNudge}
				isExitingForReplacement={isExitingForReplacement}
				onReplacementExitComplete={handleReplacementExitComplete}
			/>
		</React.Suspense>
	);
};
